import React, { useEffect, useState } from 'react';
import { HealthBuddy } from '../HealthBuddy';
import { VoiceBar, VoiceActivity, useReadThenListen } from '../VoiceBar';
import { ArrowLeftIcon, CheckIcon, ChevronRightIcon, DownloadIcon, DropletIcon, MicIcon, MinusIcon, MoonIcon, PauseIcon, PencilIcon, PlayIcon, WaveIcon } from '../icons';
import { SESSIONS, SOUNDSCAPES, SLEEP_WEEK, BEDTIMES, WAKE_TIMES, QUALITY_LABELS, RelaxSession, SoundscapeId } from '../../data/relax';
import { TINTS } from '../../data/mock';
export type RelaxView = 'picker' | 'player' | 'sleep-log';

/* conversational-entry targets (RESULT_CARDS .start):
     'wind-down'   → the Wind-down player, already playing
     'sleep-log'   → the sleep log form
     'rain-sounds' → the Wind-down player, rain pre-selected, already playing */
export type RelaxStart = 'picker' | 'wind-down' | 'sleep-log' | 'rain-sounds';

/**
 * Relaxation & Sleep bottom modal — card-flows §5.
 *
 *   1. Picker     — two zones: Tonight (4 narrated sessions) + Last night (sleep log)
 *   2. Player     — full-height calm view: dimmed warm palette, big play/pause,
 *                   progress ring, Sunny breathing with the session, soundscapes,
 *                   auto-dim after 10 seconds
 *   3. Sleep log  — bedtime + wake time + 5-moon quality + note → celebration
 *
 * Conversational entry skips the picker (startAt prop):
 *   "I can't sleep"           → Wind-down player, already playing
 *   "log my sleep"            → sleep log form directly
 *   "play the rain sounds one" → player with the rain soundscape pre-selected
 *
 * Voice mode (the Sunny avatar in the modal header, shared VoiceBar pattern):
 * Sunny reads the picker aloud and listens for a session name. The player
 * deliberately has no VoiceBar — the session itself is Sunny's voice.
 */
export function RelaxModal({
  onDone,
  startAt = 'picker',
  voiceMode = false,
  onVoiceActivity,
  onViewChange
}: {
  onDone?: () => void;
  startAt?: RelaxStart;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
  onViewChange?: (v: RelaxView) => void;
}) {
  const cameFromPicker = startAt === 'picker';
  const initialView: RelaxView = startAt === 'picker' ? 'picker' : startAt === 'sleep-log' ? 'sleep-log' : 'player';
  const [view, setViewState] = useState<RelaxView>(initialView);
  const [sessionId, setSessionId] = useState('wind-down');
  /* "play the rain sounds one" → the soundscape arrives pre-selected */
  const [soundscape] = useState<SoundscapeId>(startAt === 'rain-sounds' ? 'rain' : 'none');
  const setView = (v: RelaxView) => {
    setViewState(v);
    onViewChange?.(v);
  };
  const openSession = (id: string) => {
    setSessionId(id);
    setView('player');
  };
  const session = SESSIONS.find(s => s.id === sessionId) ?? SESSIONS[0];
  const backToPicker = cameFromPicker ? () => setView('picker') : undefined;
  if (view === 'player') return <CalmPlayer session={session} initialSoundscape={soundscape}
  /* conversational entry → already playing; touch entry → a calm, deliberate press of play */ autoPlay={!cameFromPicker} onBack={backToPicker} data-id="element-563" />;
  if (view === 'sleep-log') return <SleepLogForm onBack={backToPicker} onDone={onDone} data-id="element-564" />;
  return <SessionPicker onPickSession={openSession} onLogSleep={() => setView('sleep-log')} voiceMode={voiceMode} onVoiceActivity={onVoiceActivity} data-id="element-565" />;
}

/* ============================================================
   1 — the session picker (two zones: Tonight + Last night)
   ============================================================ */

function SessionPicker({
  onPickSession,
  onLogSleep,
  voiceMode = false,
  onVoiceActivity
}: {
  onPickSession: (id: string) => void;
  onLogSleep: () => void;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
}) {
  const phase = useReadThenListen(voiceMode, onVoiceActivity, 2800);
  return <div className="px-5 pb-10 pt-2" data-id="element-566">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-lilac-deep" data-id="element-567">Relax & Sleep</p>
      <h2 className="font-display mt-1 pr-20 text-[22px] font-semibold leading-tight text-ink" data-id="element-568">Time to wind down</h2>
      <p className="mt-1.5 text-[13px] leading-snug text-ink-soft" data-id="element-569">
        Narrated sessions for tonight — and a place to log how last night went.
      </p>

      {/* voice mode — Sunny reads the options, then listens */}
      {voiceMode && <VoiceBar mode={phase} hint={phase === 'reading' ? 'I have four sessions for tonight — a wind-down, a sleep story, box breathing and a body scan. Or we can log last night’s sleep…' : 'Listening — say a session, or “log my sleep”'} sayables={phase === 'listening' ? [{
      label: 'Wind-down',
      onSay: () => onPickSession('wind-down')
    }, {
      label: 'Sleep story',
      onSay: () => onPickSession('sleep-story')
    }, {
      label: 'Box breathing',
      onSay: () => onPickSession('box-breathing')
    }, {
      label: 'Log my sleep',
      onSay: onLogSleep
    }] : undefined} data-id="element-570" />}

      {/* ---- zone 1: Tonight ---- */}
      <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.14em] text-ink-faint" data-id="element-571">Tonight</p>
      <div className="dhp-stagger mt-2.5 flex flex-col gap-2.5" data-id="element-572">
        {SESSIONS.map(session => {
        const tint = TINTS[session.tint];
        const Icon = session.icon;
        return <button key={session.id} onClick={() => onPickSession(session.id)} className={`flex items-center gap-3.5 rounded-card border ${tint.border} bg-cream p-4 text-left shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]`} data-id="element-573">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint.solid} text-cream shadow-card`} data-id="element-574">
                <Icon size={22} data-id="element-575" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-576">
                <span className="flex items-baseline gap-2" data-id="element-577">
                  <h3 className="font-display text-[16px] font-semibold leading-tight text-ink" data-id="element-578">{session.title}</h3>
                  <span className="text-[11px] font-bold text-ink-faint" data-id="element-579">{session.minutes} min</span>
                </span>
                <p className={`mt-0.5 truncate text-[12px] font-medium ${tint.text}`} data-id="element-580">{session.kind}</p>
                {/* the "downloaded" badge — ties into Settings → narration downloads */}
                <p className="mt-1 flex items-center gap-1 text-[10.5px] font-semibold text-ink-faint" data-id="element-581">
                  {session.downloaded ? <>
                      <CheckIcon size={10} strokeWidth={2.6} data-id="element-582" />
                      On device · {session.narrator}
                    </> : <>
                      <DownloadIcon size={10} strokeWidth={2.4} data-id="element-583" />
                      Streams · {session.narrator}
                    </>}
                </p>
              </span>
              <ChevronRightIcon size={16} className="shrink-0 text-ink-faint" data-id="element-584" />
            </button>;
      })}
      </div>

      {/* ---- zone 2: Last night ---- */}
      <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.14em] text-ink-faint" data-id="element-585">Last night</p>
      <button onClick={onLogSleep} className="mt-2.5 flex w-full items-center gap-3.5 rounded-card border-2 border-dashed border-lilac bg-lilac-soft p-4 text-left transition-all duration-300 hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-586">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-lilac text-cream shadow-card" data-id="element-587">
          <PencilIcon size={20} data-id="element-588" />
        </span>
        <span className="min-w-0 flex-1" data-id="element-589">
          <h3 className="font-display text-[16px] font-semibold leading-tight text-ink" data-id="element-590">How did you sleep?</h3>
          <p className="mt-0.5 text-[12px] font-medium text-lilac-deep" data-id="element-591">Not logged yet — takes about 20 seconds</p>
        </span>
        <ChevronRightIcon size={16} className="shrink-0 text-ink-faint" data-id="element-592" />
      </button>

      {/* the last week at a glance — a moon per night, sized by quality */}
      <div className="mt-3 rounded-card bg-linen p-3.5" data-id="element-593">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-faint" data-id="element-594">Your week</p>
        <div className="mt-2.5 flex items-end justify-between px-1" data-id="element-595">
          {SLEEP_WEEK.map((night, i) => <div key={i} className="flex flex-col items-center gap-1.5" data-id="element-596">
              <span className="flex h-6 items-end text-lilac" style={{
            opacity: 0.3 + night.quality * 0.14
          }} data-id="element-597">
                <MoonIcon size={11 + night.quality * 2} data-id="element-598" />
              </span>
              <span className="text-[9.5px] font-bold text-ink-faint" data-id="element-599">{night.day}</span>
            </div>)}
        </div>
      </div>

      {/* the voice shortcut — everything in the picker can be said instead */}
      <div className="mt-5 flex items-center gap-3 rounded-card bg-linen p-3.5" data-id="element-600">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal text-cream" data-id="element-601">
          <MicIcon size={16} data-id="element-602" />
        </span>
        <p className="text-[12px] leading-snug text-ink-soft" data-id="element-603">
          Or just tell Sunny — <span className="font-bold text-ink" data-id="element-604">“I can't sleep”</span> or{' '}
          <span className="font-bold text-ink" data-id="element-605">“log my sleep”</span>
        </p>
      </div>
    </div>;
}

/* ============================================================
   2 — the calm session player
   ============================================================ */
/* Full-height night panel inside the cream sheet — dimmed warm palette,
   one big control, Sunny breathing at the session's pace. After 10 seconds
   of playing the screen dims itself; any tap wakes it. */

function CalmPlayer({
  session,
  initialSoundscape = 'none',
  autoPlay = false,
  onBack
}: {
  session: RelaxSession;
  initialSoundscape?: SoundscapeId;
  autoPlay?: boolean;
  onBack?: () => void;
}) {
  const [playing, setPlaying] = useState(autoPlay);
  /* conversational entry arrives "already playing" — a little way in */
  const [elapsed, setElapsed] = useState(autoPlay ? 8 : 0);
  const [soundscape, setSoundscape] = useState<SoundscapeId>(initialSoundscape);
  const [dimmed, setDimmed] = useState(false);
  const total = session.minutes * 60;

  /* mock playback — time runs at 10× so the prototype visibly progresses */
  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => setElapsed(e => Math.min(total, e + 10)), 1000);
    return () => clearInterval(t);
  }, [playing, total]);

  /* auto-dim after 10 seconds of uninterrupted playing — tap anywhere to wake */
  useEffect(() => {
    if (!playing || dimmed) return;
    const t = setTimeout(() => setDimmed(true), 10000);
    return () => clearTimeout(t);
  }, [playing, dimmed]);
  const wake = () => setDimmed(false);
  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const R = 84;
  const C = 2 * Math.PI * R;
  const progress = Math.min(1, elapsed / total);
  const soundscapeIcon = (id: SoundscapeId) => id === 'rain' ? <DropletIcon size={13} data-id="element-606" /> : id === 'waves' ? <WaveIcon size={13} data-id="element-607" /> : <MinusIcon size={13} data-id="element-608" />;
  return <div className="px-4 pb-6 pt-2" onClick={wake} data-id="element-609">
      {/* the night panel — a window into the dimmed warm palette */}
      <div className="relative flex min-h-[620px] flex-col items-center overflow-hidden rounded-[24px] px-6 pb-8 pt-5 text-center" style={{
      background: 'linear-gradient(180deg, #39304A 0%, #2A2438 55%, #211C2D 100%)'
    }} data-id="element-610">
        {/* header row */}
        <div className="flex w-full items-center justify-between" data-id="element-611">
          {onBack ? <button onClick={onBack} className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFFFFF14] text-[#D8CFE8] transition-transform active:scale-90" data-id="element-612">
              <ArrowLeftIcon size={15} data-id="element-613" />
            </button> : <span className="h-8 w-8" data-id="element-614" />}
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#B9A8E0]" data-id="element-615">Relax & Sleep</p>
          <span className="h-8 w-8" data-id="element-616" />
        </div>

        {/* everything below softens when the screen auto-dims */}
        <div className="flex w-full flex-1 flex-col items-center transition-opacity duration-[2500ms]" style={{
        opacity: dimmed ? 0.3 : 1
      }} data-id="element-617">
          {/* session identity */}
          <h2 className="font-display mt-5 text-[28px] font-semibold leading-tight text-[#F5F0E8]" data-id="element-618">{session.title}</h2>
          <p className="mt-1 text-[12.5px] font-medium text-[#B9A8E0]" data-id="element-619">
            {session.minutes} min · {session.narrator}
          </p>

          {/* Sunny keeps the user company, breathing at the session's calm pace */}
          <div className="mt-6" data-id="element-620">
            <HealthBuddy size={76} mood="calm" data-id="element-621" />
          </div>

          {/* what Sunny says as the session starts */}
          <p className="mt-5 max-w-[260px] text-[13.5px] italic leading-relaxed text-[#CEC3E2]" data-id="element-622">“{session.intro}”</p>

          {/* progress ring + the one big control */}
          <div className="relative mt-7" data-id="element-623">
            <svg width={196} height={196} viewBox="0 0 196 196" className="-rotate-90" data-id="element-624">
              <circle cx="98" cy="98" r={R} fill="none" stroke="#FFFFFF1F" strokeWidth="5" data-id="element-625" />
              <circle cx="98" cy="98" r={R} fill="none" stroke="var(--dhp-lilac)" strokeWidth="5" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - progress)} style={{
              transition: 'stroke-dashoffset 1s linear'
            }} data-id="element-626" />
            </svg>
            <button onClick={() => setPlaying(p => !p)} className="absolute left-1/2 top-1/2 flex h-[120px] w-[120px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-lilac text-cream shadow-card transition-transform hover:scale-[1.04] active:scale-95" data-id="element-627">
              {playing ? <PauseIcon size={38} data-id="element-628" /> : <PlayIcon size={38} className="ml-1.5" data-id="element-629" />}
            </button>
          </div>

          {/* time */}
          <p className="font-display mt-4 text-[15px] font-medium text-[#D8CFE8]" data-id="element-630">
            {fmt(elapsed)} <span className="text-[#8F82AC]" data-id="element-631">of {fmt(total)}</span>
          </p>

          {/* soundscape toggle — rain / waves / none */}
          <div className="mt-6 w-full" data-id="element-632">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8F82AC]" data-id="element-633">Soundscape</p>
            <div className="mt-2.5 flex justify-center gap-2" data-id="element-634">
              {SOUNDSCAPES.map(s => <button key={s.id} onClick={() => setSoundscape(s.id)} className={`flex items-center gap-1.5 rounded-full px-4 py-2.5 text-[12.5px] font-bold transition-all ${soundscape === s.id ? 'bg-lilac text-cream shadow-card' : 'bg-[#FFFFFF14] text-[#CEC3E2] hover:bg-[#FFFFFF22]'}`} data-id="element-635">
                  {soundscapeIcon(s.id)}
                  {s.label}
                </button>)}
            </div>
          </div>

          {/* offline state + auto-dim note */}
          <div className="mt-auto pt-7" data-id="element-636">
            <p className="flex items-center justify-center gap-1.5 text-[11.5px] font-semibold text-[#8F82AC]" data-id="element-637">
              {session.downloaded ? <>
                  <CheckIcon size={12} strokeWidth={2.4} data-id="element-638" />
                  On your device — plays even offline
                </> : <>
                  <DownloadIcon size={12} strokeWidth={2.2} data-id="element-639" />
                  Streaming — download it in Settings for offline
                </>}
            </p>
            <p className="mt-2 text-[10.5px] font-medium text-[#6E6388]" data-id="element-640">
              {dimmed ? 'Screen dimmed for sleep — tap anywhere to wake it' : playing ? 'The screen will dim itself in a moment' : 'The screen dims while a session plays'}
            </p>
          </div>
        </div>
      </div>
    </div>;
}

/* ============================================================
   3 — the sleep log (bedtime · wake time · moons · note → celebration)
   ============================================================ */

function SleepLogForm({
  onBack,
  onDone
}: {
  onBack?: () => void;
  onDone?: () => void;
}) {
  const [bedtime, setBedtime] = useState('10:30 pm');
  const [wakeTime, setWakeTime] = useState('7:00 am');
  const [quality, setQuality] = useState(0); /* 0 = not chosen yet */
  const [logged, setLogged] = useState(false);
  if (logged) return <SleepCelebration quality={quality} onDone={onDone} data-id="element-641" />;
  return <div className="px-5 pb-10 pt-2" data-id="element-642">
      {/* header */}
      <div className="flex items-center gap-2" data-id="element-643">
        {onBack && <button onClick={onBack} className="flex h-8 w-8 items-center justify-center rounded-full bg-linen text-ink-soft transition-transform active:scale-90" data-id="element-644">
            <ArrowLeftIcon size={15} data-id="element-645" />
          </button>}
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-lilac-deep" data-id="element-646">Relax & Sleep · Sleep log</p>
      </div>
      <h2 className="font-display mt-1 pr-20 text-[22px] font-semibold leading-tight text-ink" data-id="element-647">How did you sleep?</h2>

      {/* bedtime */}
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-648">When did you go to bed?</p>
      <div className="no-scrollbar mt-2 flex gap-2 overflow-x-auto" data-id="element-649">
        {BEDTIMES.map(t => <button key={t} onClick={() => setBedtime(t)} className={`shrink-0 rounded-full px-3.5 py-2.5 text-[12.5px] font-bold transition-all ${bedtime === t ? 'bg-lilac text-cream shadow-card' : 'bg-linen text-ink-soft hover:bg-linen-deep'}`} data-id="element-650">
            {t}
          </button>)}
      </div>

      {/* wake time */}
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-651">And when did you wake up?</p>
      <div className="no-scrollbar mt-2 flex gap-2 overflow-x-auto" data-id="element-652">
        {WAKE_TIMES.map(t => <button key={t} onClick={() => setWakeTime(t)} className={`shrink-0 rounded-full px-3.5 py-2.5 text-[12.5px] font-bold transition-all ${wakeTime === t ? 'bg-lilac text-cream shadow-card' : 'bg-linen text-ink-soft hover:bg-linen-deep'}`} data-id="element-653">
            {t}
          </button>)}
      </div>

      {/* quality — 5 moons */}
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-654">How did it feel?</p>
      <div className="mt-2 rounded-card bg-linen p-4" data-id="element-655">
        <div className="flex justify-center gap-2.5" data-id="element-656">
          {[1, 2, 3, 4, 5].map(n => <button key={n} onClick={() => setQuality(n)} className={`flex h-12 w-12 items-center justify-center rounded-full transition-all active:scale-90 ${quality >= n ? 'bg-lilac text-cream shadow-card' : 'bg-cream text-ink-faint'}`} data-id="element-657">
              <MoonIcon size={22} data-id="element-658" />
            </button>)}
        </div>
        <p className="mt-3 text-center text-[13px] font-bold text-lilac-deep" data-id="element-659">
          {quality > 0 ? QUALITY_LABELS[quality - 1] : 'Tap the moons'}
        </p>
      </div>

      {/* note */}
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-660">Anything to remember?</p>
      <div className="mt-2 rounded-2xl bg-linen p-4" data-id="element-661">
        <p className="text-[13.5px] text-ink-faint" data-id="element-662">Add a note, or dictate one…</p>
      </div>

      {/* confirm — needs a quality before it goes */}
      <button onClick={() => quality > 0 && setLogged(true)} className={`mt-7 w-full rounded-full py-4 text-[15px] font-bold text-cream shadow-card transition-transform ${quality > 0 ? 'bg-lilac hover:scale-[1.02] active:scale-[0.98]' : 'bg-linen-deep'}`} data-id="element-663">
        Log it
      </button>
      <p className="mt-2.5 text-center text-[11.5px] font-medium text-ink-faint" data-id="element-664">
        You can also just say “I slept badly” and Sunny will fill this in.
      </p>
    </div>;
}
function SleepCelebration({
  quality,
  onDone
}: {
  quality: number;
  onDone?: () => void;
}) {
  return <div className="flex flex-col items-center px-5 pb-12 pt-6 text-center" data-id="element-665">
      <HealthBuddy size={96} mood="celebrating" data-id="element-666" />
      <h2 className="font-display mt-5 text-[24px] font-semibold text-ink" data-id="element-667">Sleep logged!</h2>
      <p className="mt-2 max-w-[280px] text-[14.5px] leading-relaxed text-ink-soft" data-id="element-668">
        {QUALITY_LABELS[quality - 1]} — that's 8 nights in a row of keeping track. Patterns like this help you and
        your care team spot what works.
      </p>
      {/* the week, now with last night added */}
      <div className="mt-5 flex items-end gap-1.5" data-id="element-669">
        {[...SLEEP_WEEK.map(n => n.quality), quality].map((q, i) => <div key={i} className={`flex h-9 w-9 items-center justify-center rounded-full ${i === SLEEP_WEEK.length ? 'bg-lilac text-cream' : 'bg-lilac-soft text-lilac-deep'}`} data-id="element-670">
            <MoonIcon size={12 + q} data-id="element-671" />
          </div>)}
      </div>
      <button onClick={onDone} className="mt-7 w-full rounded-full bg-teal py-4 text-[15px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-672">
        Done
      </button>
    </div>;
}